import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { requestAgentsAndAssignees, transferGroup } from 'api/operations/groupService';
import { action, autorun, computed, makeObservable, observable, runInAction } from 'mobx';
import { ParentStore, SearchAgencyDelegate } from 'store/pages/bi-groups-dashboard/searchAgencyDelegate';
import { SelectedGroup } from 'store/pages/bi-groups-dashboard/types';
import { Assignee } from 'types/groupService';

interface CreationParams {
  parentStore: ParentStore;
  getSelectedGroup: () => SelectedGroup;
  closeModal: () => void;
}

interface FormFields extends DefaultFormFields {
  agencyId: FormControl<string>;
  aorId: FormControl<string>;
}

export default class MoveGroupModalStore {
  private _parentStore: ParentStore;
  private _isAorSectionLoading = false;
  private _agencyAors: Assignee[] = [];
  private _getSelectedGroup: () => SelectedGroup;
  private _closeModal: () => void;

  private async _moveGroup(): Promise<void> {
    await runInAction(async () => {
      const currentAor = this.agencyAors.find((aor) => aor.id === this.form.fields.aorId.value);
      await runInAction(async () => {
        await transferGroup(
          this._getSelectedGroup().id,
          this.form.fields.agencyId.value,
          currentAor?.npn || '',
          currentAor?.tin,
        );
      });
      this._closeModal();
      await this._parentStore.getInitialData();
    });
  }

  private _setAorLoading(loadingStatus: boolean): void {
    this._isAorSectionLoading = loadingStatus;
  }

  private _setAgencyAors(aors: Assignee[]): void {
    this._agencyAors = aors;
  }

  private async _getAgencyAors(agencyId: string): Promise<void> {
    this._setAorLoading(true);

    const { agentsOfRecord } = await requestAgentsAndAssignees(agencyId);
    this._setAgencyAors(agentsOfRecord);
    this._setAorLoading(false);
  }

  public form = new FormController<FormFields>(
    { agencyId: new FormControl('', { optional: false }), aorId: new FormControl('') },
    { onSubmit: this._moveGroup.bind(this) },
  );

  public searchAgencyDelegate;

  public get isAorSectionLoading(): boolean {
    return this._isAorSectionLoading;
  }

  public get agencyAors(): Assignee[] {
    return this._agencyAors;
  }

  public setAgencyId(agencyId: string): void {
    this.form.fields.aorId.overrideValue('');
    this.form.fields.agencyId.overrideValue(agencyId);
    if (agencyId) {
      void this._getAgencyAors(agencyId);
    }
  }

  public resetModalData(): void {
    this.form.resetForm();
    this._agencyAors = [];
    this.searchAgencyDelegate.resetSearchValue();
  }

  constructor({ parentStore, getSelectedGroup, closeModal }: CreationParams) {
    this._getSelectedGroup = getSelectedGroup;
    this._closeModal = closeModal;
    this.searchAgencyDelegate = new SearchAgencyDelegate({ parentStore });
    this._parentStore = parentStore;

    makeObservable<
      MoveGroupModalStore,
      '_isAorSectionLoading' | '_agencyAors' | '_moveGroup' | '_setAorLoading' | '_setAgencyAors' | '_getAgencyAors'
    >(this, {
      _isAorSectionLoading: observable,
      _agencyAors: observable,
      form: observable,
      searchAgencyDelegate: observable,
      isAorSectionLoading: computed,
      agencyAors: computed,
      _setAorLoading: action.bound,
      _moveGroup: action.bound,
      _setAgencyAors: action.bound,
      _getAgencyAors: action.bound,
      setAgencyId: action.bound,
      resetModalData: action.bound,
    });

    autorun(() => {
      if (this._agencyAors.length > 0) {
        this.form.addFormField('aorId', new FormControl('', { optional: false }));
      } else {
        this.form.addFormField('aorId', new FormControl('', { disabled: true }));
      }
    });
  }
}
